// Reference data for the signup onboarding wizard. Lives in interfaces so the
// canonical lists (module keys, governorates) are shared between the wizard,
// the data-service create_tenant call, the module-gated sidebar, and any
// future client/supplier forms that want a governorate dropdown.

/* ----------------------- Modules ----------------------- */

/** Canonical module keys. Mirror the rows we INSERT into `module_settings`
 *  on tenant create, and the keys the sidebar consults to show/hide groups. */
export const MODULE_KEYS = [
  'sales',
  'purchases',
  'stock',
  'payments',
  'treasury',
  'checks',
  'withholding',
  'storefront',
  'caisse',
  'pro',
] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];

/** Modules that are always-on for every tenant — they are the core of the
 *  product (you can't sell anything without sales + articles). */
export const ALWAYS_ON_MODULES: readonly ModuleKey[] = ['sales'];

/** Modules that a tenant can NEVER grant itself. They are sold/activated
 *  case by case and stay completely invisible otherwise: absent from the
 *  onboarding wizard, absent from Paramètres → Modules, and excluded from
 *  the permissive set the sidebar uses while `module_settings` is still
 *  loading (so the nav group can't even flash on first paint).
 *
 *  `pro` (Espace Pro B2B) is bespoke work built for Tunielec — every other
 *  tenant must not know it exists. Only a platform admin can flip it, via
 *  `IPlatformAdminService.setModule`; the `module_settings_restricted_guard`
 *  trigger (migration 20260802180000) enforces the same rule in the
 *  database, so hiding the card here is presentation, not the gate.
 *
 *  A tenant that already HAS the module keeps full control of its
 *  sub-features — the settings page renders a restricted module's card
 *  once it is enabled, and the DB guard only covers the top-level key. */
export const RESTRICTED_MODULES: readonly ModuleKey[] = ['pro'];

/** Default opt-in selection for a brand-new tenant. Picked to match what most
 *  Tunisian SMBs need on day one; everything else they activate later. */
export const DEFAULT_ENABLED_MODULES: readonly ModuleKey[] = [
  'sales',
  'purchases',
  'stock',
  'payments',
];

/** Catalog entry shown to the user in the wizard's module step. Translation
 *  keys point at libs/i18n/.../onboarding.modules.<key>.{title,desc}. */
export interface ModuleCatalogEntry {
  key: ModuleKey;
  /** Tabler icon name (consumed by `@/components/icons` `<DynamicIcon>`). */
  icon: string;
  /** Translation key suffix under `onboarding.modules`. */
  i18nKey: string;
  /** Whether the user can disable it in the wizard. */
  alwaysOn: boolean;
}

export const MODULE_CATALOG: readonly ModuleCatalogEntry[] = [
  { key: 'sales',       icon: 'shopping-cart', i18nKey: 'sales',       alwaysOn: true  },
  { key: 'purchases',   icon: 'shopping-bag',  i18nKey: 'purchases',   alwaysOn: false },
  { key: 'stock',       icon: 'warehouse',     i18nKey: 'stock',       alwaysOn: false },
  { key: 'payments',    icon: 'banknote',      i18nKey: 'payments',    alwaysOn: false },
  { key: 'treasury',    icon: 'landmark',      i18nKey: 'treasury',    alwaysOn: false },
  { key: 'checks',      icon: 'book-check',    i18nKey: 'checks',      alwaysOn: false },
  { key: 'withholding', icon: 'file-text',     i18nKey: 'withholding', alwaysOn: false },
  { key: 'storefront',  icon: 'globe',         i18nKey: 'storefront',  alwaysOn: false },
  { key: 'caisse',      icon: 'receipt',       i18nKey: 'caisse',      alwaysOn: false },
];

/* ----------------------- Sub-features per module ----------------------- */

/** Sub-feature keys per module. A row in `module_settings` with
 *  `module_key = '${module}.${feature}'` toggles the feature on/off.
 *
 *  Default semantics: when the row is **missing**, the feature is
 *  considered ENABLED. This keeps the database backward-compatible —
 *  existing tenants who never personalised get every sub-feature on,
 *  and we only persist explicit user overrides. */
export const MODULE_FEATURES = {
  sales: ['quotes', 'orders', 'deliveries', 'invoices', 'credit_notes', 'clients', 'representatives'],
  purchases: ['suppliers', 'orders', 'receptions', 'invoices', 'credit_notes'],
  stock: ['movements', 'vehicle_exits', 'vehicle_entries', 'warehouses', 'inventories'],
  payments: ['regular', 'recurring'],
  treasury: ['bank_accounts'],
  checks: ['checks', 'drafts'],
  withholding: ['sales', 'purchases', 'certificates'],
  storefront: [
    'orders', 'customers', 'products', 'categories', 'theme', 'pages',
    'banners', 'abandoned_carts', 'notifications', 'settings', 'analytics',
    'cod_calculator',
  ],
  caisse: ['pos', 'registers', 'sessions'],
  pro: [
    'customers', 'products', 'categories', 'commandes', 'wallets',
    'commissions', 'settings',
  ],
} as const satisfies Record<ModuleKey, readonly string[]>;

/** Union of every valid feature key (string), across all modules. We keep
 *  the per-module precision in `ModuleFeature<M>` below for call sites
 *  that know the module statically. */
export type AnyFeatureKey = {
  [M in ModuleKey]: (typeof MODULE_FEATURES)[M][number];
}[ModuleKey];

/** Feature keys for a specific module. */
export type ModuleFeature<M extends ModuleKey> = (typeof MODULE_FEATURES)[M][number];

/** Build the composite `module_settings.module_key` value used for a
 *  sub-feature row. Example: `featureSettingKey('purchases', 'invoices')`
 *  → `'purchases.invoices'`. Centralised so the hook, mutation, and any
 *  future tooling all spell the key the same way. */
export function featureSettingKey<M extends ModuleKey>(
  module: M,
  feature: ModuleFeature<M>,
): string {
  return `${module}.${feature}`;
}

/* ----------------------- Tunisian governorates ----------------------- */

/** The 24 Tunisian governorates (wilayas). ISO 3166-2:TN codes.
 *  Used for the company-address dropdown in the onboarding wizard and
 *  reusable on client/supplier address forms. */
export interface TunisianGovernorate {
  /** ISO code, e.g. 'TN-71' for Tunis. */
  code: string;
  nameFr: string;
  nameAr: string;
}

export const TUNISIAN_GOVERNORATES: readonly TunisianGovernorate[] = [
  { code: 'TN-11', nameFr: 'Tunis',          nameAr: 'تونس' },
  { code: 'TN-12', nameFr: 'Ariana',         nameAr: 'أريانة' },
  { code: 'TN-13', nameFr: 'Ben Arous',      nameAr: 'بن عروس' },
  { code: 'TN-14', nameFr: 'Manouba',        nameAr: 'منوبة' },
  { code: 'TN-21', nameFr: 'Nabeul',         nameAr: 'نابل' },
  { code: 'TN-22', nameFr: 'Zaghouan',       nameAr: 'زغوان' },
  { code: 'TN-23', nameFr: 'Bizerte',        nameAr: 'بنزرت' },
  { code: 'TN-31', nameFr: 'Béja',           nameAr: 'باجة' },
  { code: 'TN-32', nameFr: 'Jendouba',       nameAr: 'جندوبة' },
  { code: 'TN-33', nameFr: 'Kef',            nameAr: 'الكاف' },
  { code: 'TN-34', nameFr: 'Siliana',        nameAr: 'سليانة' },
  { code: 'TN-41', nameFr: 'Kairouan',       nameAr: 'القيروان' },
  { code: 'TN-42', nameFr: 'Kasserine',      nameAr: 'القصرين' },
  { code: 'TN-43', nameFr: 'Sidi Bouzid',    nameAr: 'سيدي بوزيد' },
  { code: 'TN-51', nameFr: 'Sousse',         nameAr: 'سوسة' },
  { code: 'TN-52', nameFr: 'Monastir',       nameAr: 'المنستير' },
  { code: 'TN-53', nameFr: 'Mahdia',         nameAr: 'المهدية' },
  { code: 'TN-61', nameFr: 'Sfax',           nameAr: 'صفاقس' },
  { code: 'TN-71', nameFr: 'Gafsa',          nameAr: 'قفصة' },
  { code: 'TN-72', nameFr: 'Tozeur',         nameAr: 'توزر' },
  { code: 'TN-73', nameFr: 'Kébili',         nameAr: 'قبلي' },
  { code: 'TN-81', nameFr: 'Gabès',          nameAr: 'قابس' },
  { code: 'TN-82', nameFr: 'Médenine',       nameAr: 'مدنين' },
  { code: 'TN-83', nameFr: 'Tataouine',      nameAr: 'تطاوين' },
];

/* ----------------------- Activity profile (wizard step 2) ----------------------- */

/** Where the business sells. Multi-select; each answer nudges the module
 *  preselection on the wizard's Modules step. */
export const SALES_CHANNELS = ['store', 'online', 'b2b', 'field'] as const;
export type SalesChannelKey = (typeof SALES_CHANNELS)[number];

/** How the business gets paid. Multi-select; drives checks/treasury/payments
 *  suggestions. */
export const PAYMENT_MODES = ['cash', 'checks', 'transfer', 'installments'] as const;
export type PaymentModeKey = (typeof PAYMENT_MODES)[number];

/** Answers collected by the wizard's "Votre activité" step. Everything is
 *  optional — an empty answer set yields the stock DEFAULT_ENABLED_MODULES. */
export interface OnboardingActivityAnswers {
  activitySector?: string | null;
  channels: readonly SalesChannelKey[];
  paymentModes: readonly PaymentModeKey[];
}

/** Sectors that are service businesses: no goods flowing in/out, so the
 *  stock + purchases preselection would be noise. They stay one tap away. */
const SERVICE_SECTORS: ReadonlySet<string> = new Set([
  'it_services',
  'consulting',
  'education',
  'real_estate',
]);

/** Channel → modules that light up when the channel is picked. */
const CHANNEL_MODULES: Record<SalesChannelKey, readonly ModuleKey[]> = {
  store: ['caisse'],
  online: ['storefront'],
  b2b: ['withholding'],
  field: ['stock'],
};

/** Payment mode → modules that light up when the mode is picked. */
const PAYMENT_MODE_MODULES: Record<PaymentModeKey, readonly ModuleKey[]> = {
  cash: ['caisse'],
  checks: ['checks', 'treasury'],
  transfer: ['treasury'],
  installments: ['payments'],
};

export interface ModuleSuggestions {
  /** Full module set the wizard should pre-toggle (always includes the
   *  ALWAYS_ON modules). */
  preselect: readonly ModuleKey[];
  /** Subset of `preselect` that was earned by an explicit answer — the
   *  wizard renders a "Recommandé" badge on these. */
  recommended: readonly ModuleKey[];
}

/** Turn the activity answers into a module preselection.
 *
 *  Base = DEFAULT_ENABLED_MODULES, minus stock+purchases for pure service
 *  sectors, plus every module earned by a channel/payment answer. `cash`
 *  only suggests the caisse when the business actually sells in person —
 *  a wholesaler paid cash has no use for a register. Pure function so the
 *  wizard can re-run it live as answers change. */
export function computeModuleSuggestions(
  answers: OnboardingActivityAnswers,
): ModuleSuggestions {
  const recommended = new Set<ModuleKey>();
  for (const channel of answers.channels) {
    for (const mod of CHANNEL_MODULES[channel]) recommended.add(mod);
  }
  for (const mode of answers.paymentModes) {
    if (mode === 'cash' && !answers.channels.includes('store')) continue;
    for (const mod of PAYMENT_MODE_MODULES[mode]) recommended.add(mod);
  }

  const isService =
    !!answers.activitySector && SERVICE_SECTORS.has(answers.activitySector);
  const base = DEFAULT_ENABLED_MODULES.filter(
    (m) => !isService || (m !== 'stock' && m !== 'purchases'),
  );

  const preselect = new Set<ModuleKey>([...ALWAYS_ON_MODULES, ...base, ...recommended]);
  // Keep MODULE_KEYS declaration order so the wizard's card grid and the
  // RPC payload stay stable regardless of answer order.
  const ordered = (set: ReadonlySet<ModuleKey>) =>
    MODULE_KEYS.filter((m) => set.has(m));
  return { preselect: ordered(preselect), recommended: ordered(recommended) };
}

/* ----------------------- Activity sectors ----------------------- */

/** Common activity sectors for Tunisian SMBs. Free-text fallback always
 *  available — users can also type their own sector. */
export const ACTIVITY_SECTORS_I18N: readonly string[] = [
  'retail',
  'wholesale',
  'manufacturing',
  'construction',
  'food_beverage',
  'agriculture',
  'transport_logistics',
  'it_services',
  'consulting',
  'health',
  'education',
  'hospitality',
  'beauty_personal_care',
  'automotive',
  'real_estate',
  'other',
] as const;

export type ActivitySectorKey = (typeof ACTIVITY_SECTORS_I18N)[number];

/* ----------------------- Wizard payload shape ----------------------- */

/** Shape passed from the wizard UI all the way down to the
 *  `create_tenant_for_current_user` RPC. All fields except `name` and
 *  `subdomain` are optional. */
export interface OnboardingPayload {
  // Required basics
  name: string;
  subdomain: string;

  // Optional company details (free-form except governorate)
  legalName?: string | null;
  mf?: string | null;            // Matricule fiscal
  registrationCode?: string | null; // RC
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  governorateCode?: string | null;
  activitySector?: string | null;
  rib?: string | null;
  bankName?: string | null;

  /** Module keys to enable. `sales` is always included server-side regardless. */
  enabledModules: readonly ModuleKey[];
}
